const INSPECTOR_COLLAPSED_STORAGE_KEY = 'fluveria-right-inspector-collapsed';
const POPUP_EDGE_MARGIN_X = 4;
const POPUP_EDGE_MARGIN_Y = 2;
const POPUP_MIN_WIDTH = 640;
const POPUP_MIN_HEIGHT = 320;

interface WorkspaceLayoutDeps {
  scheduleRender: () => void;
  renderGraph: () => void;
  resetPan: () => void;
}

export interface WorkspaceLayoutController {
  openNodeEditor(): void;
  closeNodeEditor(): void;
  isNodeEditorOpen(): boolean;
  hasNodeEditorFocus(): boolean;
}

function readInspectorCollapsed(): boolean {
  if (document.documentElement.dataset.rightInspectorCollapsed === 'true') return true;
  return localStorage.getItem(INSPECTOR_COLLAPSED_STORAGE_KEY) === 'true';
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function focusIsInside(element: HTMLElement): boolean {
  const active = document.activeElement;
  return !!active && element.contains(active);
}

function pointerIsInside(element: HTMLElement): boolean {
  return element.matches(':hover');
}

export function initWorkspaceLayout(deps: WorkspaceLayoutDeps): WorkspaceLayoutController {
  const shell = document.getElementById('graph-area') as HTMLElement | null;
  const workspace = document.getElementById('workspace-area') as HTMLElement | null;
  const popup = document.getElementById('graph-area-panel') as HTMLElement | null;
  const header = document.getElementById('graph-header') as HTMLElement | null;
  const closeButton = document.getElementById('node-editor-close');
  const inspector = document.getElementById('right-inspector') as HTMLElement | null;
  const inspectorToggle = document.getElementById('right-inspector-toggle');

  if (shell && popup && popup.parentElement !== shell) {
    shell.appendChild(popup);
  }

  function applyInspectorCollapsed(collapsed: boolean, persist = true): void {
    if (!workspace || !inspector) return;
    workspace.classList.toggle('right-inspector-collapsed', collapsed);
    inspector.classList.toggle('collapsed', collapsed);
    if (inspectorToggle) {
      inspectorToggle.setAttribute('aria-expanded', String(!collapsed));
      inspectorToggle.setAttribute('aria-label', collapsed ? 'Expand inspector' : 'Collapse inspector');
    }
    if (persist) localStorage.setItem(INSPECTOR_COLLAPSED_STORAGE_KEY, String(collapsed));
    delete document.documentElement.dataset.rightInspectorCollapsed;
    deps.scheduleRender();
  }

  function resetPopupGeometry(): void {
    if (!shell || !popup) return;
    const shellRect = shell.getBoundingClientRect();
    const viewport = document.getElementById('viewport-area') as HTMLElement | null;
    const rect = viewport?.getBoundingClientRect() ?? shellRect;
    const width = clamp(Math.round(rect.width * 0.72), POPUP_MIN_WIDTH, Math.max(POPUP_MIN_WIDTH, rect.width - POPUP_EDGE_MARGIN_X * 2));
    const height = clamp(Math.round(rect.height * 0.72), 420, Math.max(420, rect.height - POPUP_EDGE_MARGIN_Y * 2));
    const viewportLeft = rect.left - shellRect.left;
    const viewportTop = rect.top - shellRect.top;
    popup.style.width = `${width}px`;
    popup.style.height = `${height}px`;
    popup.style.left = `${viewportLeft + Math.max(POPUP_EDGE_MARGIN_X, Math.round((rect.width - width) / 2))}px`;
    popup.style.top = `${viewportTop + Math.max(POPUP_EDGE_MARGIN_Y, Math.round((rect.height - height) / 2))}px`;
  }

  function openNodeEditor(): void {
    if (!popup) return;
    deps.resetPan();
    if (popup.hidden) {
      resetPopupGeometry();
      popup.hidden = false;
    }
    requestAnimationFrame(() => deps.renderGraph());
    popup.focus({ preventScroll: true });
  }

  function closeNodeEditor(): void {
    if (!popup) return;
    popup.hidden = true;
  }

  function dragPopup(e: PointerEvent): void {
    if (!shell || !popup || !header) return;
    if ((e.target as HTMLElement | null)?.closest('button, select, input, textarea, summary, .graph-slot-actions')) return;
    const startShell = shell.getBoundingClientRect();
    const start = popup.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = start.left - startShell.left;
    const startTop = start.top - startShell.top;

    header.setPointerCapture(e.pointerId);
    const move = (moveEvent: PointerEvent): void => {
      const nextLeft = clamp(startLeft + moveEvent.clientX - startX, POPUP_EDGE_MARGIN_X, startShell.width - start.width - POPUP_EDGE_MARGIN_X);
      const nextTop = clamp(startTop + moveEvent.clientY - startY, POPUP_EDGE_MARGIN_Y, startShell.height - start.height - POPUP_EDGE_MARGIN_Y);
      popup.style.left = `${nextLeft}px`;
      popup.style.top = `${nextTop}px`;
    };
    const done = (): void => {
      header.removeEventListener('pointermove', move);
      header.removeEventListener('pointerup', done);
      header.removeEventListener('pointercancel', done);
    };
    header.addEventListener('pointermove', move);
    header.addEventListener('pointerup', done);
    header.addEventListener('pointercancel', done);
  }

  function resizePopup(e: PointerEvent, handle: HTMLElement): void {
    if (!shell || !popup) return;
    const shellRect = shell.getBoundingClientRect();
    const start = popup.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const dirX = Number(handle.dataset.resizeX ?? 0);
    const dirY = Number(handle.dataset.resizeY ?? 0);
    const startLeft = start.left - shellRect.left;
    const startTop = start.top - shellRect.top;

    handle.setPointerCapture(e.pointerId);
    const move = (moveEvent: PointerEvent): void => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      if (dirX > 0) {
        const maxWidth = shellRect.width - startLeft - POPUP_EDGE_MARGIN_X;
        popup.style.width = `${clamp(start.width + dx, POPUP_MIN_WIDTH, maxWidth)}px`;
      } else if (dirX < 0) {
        const nextWidth = clamp(start.width - dx, POPUP_MIN_WIDTH, start.right - shellRect.left - POPUP_EDGE_MARGIN_X);
        popup.style.width = `${nextWidth}px`;
        popup.style.left = `${clamp(startLeft + start.width - nextWidth, POPUP_EDGE_MARGIN_X, startLeft + start.width - POPUP_MIN_WIDTH)}px`;
      }

      if (dirY > 0) {
        const maxHeight = shellRect.height - startTop - POPUP_EDGE_MARGIN_Y;
        popup.style.height = `${clamp(start.height + dy, POPUP_MIN_HEIGHT, maxHeight)}px`;
      } else if (dirY < 0) {
        const nextHeight = clamp(start.height - dy, POPUP_MIN_HEIGHT, start.bottom - shellRect.top - POPUP_EDGE_MARGIN_Y);
        popup.style.height = `${nextHeight}px`;
        popup.style.top = `${clamp(startTop + start.height - nextHeight, POPUP_EDGE_MARGIN_Y, startTop + start.height - POPUP_MIN_HEIGHT)}px`;
      }
      deps.renderGraph();
    };
    const done = (): void => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', done);
      handle.removeEventListener('pointercancel', done);
      deps.scheduleRender();
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', done);
    handle.addEventListener('pointercancel', done);
  }

  function createResizeHandles(): void {
    if (!popup) return;
    const handles = [
      ['n', 0, -1], ['e', 1, 0], ['s', 0, 1], ['w', -1, 0],
      ['ne', 1, -1], ['se', 1, 1], ['sw', -1, 1], ['nw', -1, -1],
    ] as const;
    for (const [name, x, y] of handles) {
      const handle = document.createElement('div');
      handle.className = `node-editor-resize node-editor-resize-${name}`;
      handle.dataset.resizeX = String(x);
      handle.dataset.resizeY = String(y);
      handle.setAttribute('aria-hidden', 'true');
      handle.addEventListener('pointerdown', e => resizePopup(e, handle));
      popup.appendChild(handle);
    }
  }

  closeButton?.addEventListener('click', closeNodeEditor);
  header?.addEventListener('pointerdown', dragPopup);
  createResizeHandles();
  popup?.addEventListener('mousedown', () => popup.focus({ preventScroll: true }));
  popup?.setAttribute('tabindex', '-1');

  inspectorToggle?.addEventListener('click', () => {
    applyInspectorCollapsed(!workspace?.classList.contains('right-inspector-collapsed'));
  });
  applyInspectorCollapsed(readInspectorCollapsed(), false);

  return {
    openNodeEditor,
    closeNodeEditor,
    isNodeEditorOpen(): boolean {
      return !!popup && !popup.hidden;
    },
    hasNodeEditorFocus(): boolean {
      return !!popup && !popup.hidden && (focusIsInside(popup) || pointerIsInside(popup));
    },
  };
}
